import * as React from 'react';

import classnames from 'classnames';
import moment from 'moment';

import _ from 'underscore';

import {IconButton} from '../button/icon-button/icon-button';
import {TimesCircleIcon, TickCircleIcon, TrashIcon} from '../icon';
// @ts-expect-error
import {LocationEnum, Popover} from '../popover/popover';
import {RETURN} from '../utils/keys';

import type {CannedOption} from './canned-options';
import {DateTimePicker} from './date-time-picker';
import styles from './date-time-picker-popover.module.css';

const DATE_FORMAT = 'MMMM Do, YYYY';
const TIME_FORMAT = 'h:mm A';
const INPUT_FORMAT_WITH_TIME = `${DATE_FORMAT} [at] ${TIME_FORMAT}`;
const INPUT_FORMAT_WITHOUT_TIME = DATE_FORMAT;

/**
 * Get a date object ignoring the time portion.
 * @param  {Date} date The date to sanitize
 * @return {Date}      The sanitized date
 */
function dateWithoutTime(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    return d;
}

/*
 * Get a date object ignoring the seconds of the time portion.
 */
function dateWithoutSeconds(date: Date): Date {
    const d = new Date(date);
    d.setSeconds(0, 0);

    return d;
}

/**
 * Validate a given date is greater than the given minDate and less than the
 * given maxDate.
 *
 * @param  {Date} date    The date to check
 * @param  {Date} minDate The min date, or undefined if no min date
 * @param  {Date} maxDate The max date, or undefined if no max date
 * @param  {Boolean} shouldOmitTime Should we ignore time or not
 * @return {Boolean}      return the equality conditions of minDate and maxDate.
 */
function validateMinMax(
    date: Date,
    minDate: Date | null | undefined,
    maxDate: Date | null | undefined,
    shouldOmitTime: boolean
): boolean {
    if (!(date instanceof Date)) return true;

    const dateCheck = shouldOmitTime ? dateWithoutTime(date) : dateWithoutSeconds(date);

    let isValidMin = true;
    let isValidMax = true;

    if (minDate) {
        const minDateCheck = shouldOmitTime
            ? dateWithoutTime(minDate)
            : dateWithoutSeconds(minDate);
        if (minDateCheck instanceof Date) {
            isValidMin = dateCheck.getTime() >= minDateCheck.getTime();
        }
    }

    if (maxDate) {
        const maxDateCheck = shouldOmitTime
            ? dateWithoutTime(maxDate)
            : dateWithoutSeconds(maxDate);
        if (maxDateCheck instanceof Date) {
            isValidMax = dateCheck.getTime() <= maxDateCheck.getTime();
        }
    }

    return isValidMin && isValidMax;
}

type Props = {
    anchor: HTMLElement;
    location: LocationEnum;
    shouldOmitTime: boolean;
    value: Date;
    minDate?: Date;
    maxDate?: Date;
    displayFormat?: string;
    onCancel: () => void;
    onConfirm: (arg1: Date) => void;
    onClear?: () => void;
    cannedOptions?: CannedOption[];
    saveDateOnYearChange?: boolean;
    preventPropagation?: boolean;
};

type State = {
    displayDate: string;
    isValidSelection: boolean;
    isValidDate: boolean;
    invalidShakeAnimation: boolean;
};

export class DateTimePickerPopover extends React.PureComponent<Props, State> {
    animateInvalidDuration = 800;

    initialDateParse = true;
    initialRender = true;

    inputRef: HTMLInputElement | null | undefined;
    datePickerRef: DateTimePicker | null | undefined;

    invalidFeedbackTimeout: number | null | undefined;

    static defaultProps = {
        location: 'top',
        shouldOmitTime: false,
        value: moment().startOf('minute').toDate(),
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            displayDate: this.getFormattedDate(props.value),
            isValidSelection: true,
            isValidDate: true,
            invalidShakeAnimation: false,
        };

        this.initialDateParse = true;
        this.initialRender = true;
    }

    componentDidMount() {
        this.focusTextfield();
        this.initialRender = false;
    }

    render() {
        const inputStyleName =
            this.state.isValidSelection && this.state.isValidDate
                ? styles.textfield
                : styles['textfield--invalid'];
        const popoverStyleName = classnames(styles.popover, {
            [styles['invalid-shake']]: this.state.invalidShakeAnimation,
            [styles['animate-in']]: this.initialRender,
        });

        return (
            <Popover
                anchor={this.props.anchor}
                location={this.props.location}
                noDefaultStyling={true}
                offsetPosition={15}
                onBlur={this.props.onCancel}
                className={popoverStyleName}
            >
                <div
                    onClick={this.props.preventPropagation ? this.handleStopPropagation : undefined}
                >
                    <input
                        autoComplete='off'
                        ref={(node) => {
                            this.inputRef = node;
                        }}
                        value={this.state.displayDate}
                        onChange={this.handleChange}
                        onKeyUp={this.handleKeyUp}
                        className={inputStyleName}
                    />
                    {this.renderActionButtons()}
                    <div>
                        <DateTimePicker
                            type='popover-picker'
                            // @ts-expect-error
                            textFieldClassName=''
                            ref={(c) => {
                                this.datePickerRef = c;
                            }}
                            shouldOmitTime={this.props.shouldOmitTime}
                            value={this.state.displayDate}
                            onUpdate={this.handlePickerUpdate}
                            onSelectDate={this.handlePickerSelectDate}
                            cannedOptions={this.props.cannedOptions}
                            maxDate={this.props.maxDate}
                            minDate={this.props.minDate}
                            saveDateOnYearChange={this.props.saveDateOnYearChange}
                        />
                    </div>
                </div>
            </Popover>
        );
    }

    renderActionButtons = () => {
        const {isValidDate, isValidSelection} = this.state;

        return (
            <div className={styles['actions-container']}>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        this.handleConfirm();
                    }}
                    type='button'
                    // @ts-expect-error
                    disabled={this.state.displayDate && (!isValidSelection || !isValidDate)}
                    className={styles['button-confirm']}
                >
                    <TickCircleIcon style={{height: 20}} size={20} />
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        this.props.onCancel();
                    }}
                    type='button'
                    className={styles['button-cancel']}
                >
                    <TimesCircleIcon />
                </button>
                {this.props.onClear && (
                    <div className='pl-4 pt-4'>
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                this.props.onClear && this.props.onClear();
                            }}
                            variant='danger'
                            icon={TrashIcon}
                            noPadding={true}
                        />
                    </div>
                )}
            </div>
        );
    };

    handleChange = (e: React.ChangeEvent<any>) => {
        this.setState({
            displayDate: e.target.value,
            isValidSelection: true,
            isValidDate: true,
        });
    };

    handleKeyUp = (e: React.KeyboardEvent<any>) => {
        if (e.keyCode === RETURN) {
            this.handleConfirm();
        }
    };

    handleConfirm = _.throttle(
        () => {
            const datePickerRef = this.datePickerRef;
            if (!datePickerRef) {
                // This should never happen, but is needed to make flow happy.
                this.props.onCancel();

                return;
            }
            const value = datePickerRef.getCurrentSelection();

            if (!this.state.displayDate && this.props.onClear) {
                this.props.onClear();

                return;
            }

            if (
                this.state.isValidSelection &&
                validateMinMax(
                    value,
                    this.props.minDate,
                    this.props.maxDate,
                    this.props.shouldOmitTime
                )
            ) {
                this.props.onConfirm(value);
            } else {
                this.renderInvalidSelectionFeedback();
            }
        },
        500,
        {trailing: false, leading: true}
    );

    handlePickerSelectDate = (val?: Date | null) => {
        const isValid = val
            ? validateMinMax(val, this.props.minDate, this.props.maxDate, this.props.shouldOmitTime)
            : false;

        if (
            !isValid &&
            val &&
            this.props.maxDate &&
            moment(val).isSame(this.props.maxDate, 'day')
        ) {
            // The current time is too late, but the day is okay, so just use the max.
            this.setState({
                displayDate: val ? this.getFormattedDate(this.props.maxDate) : '',
                isValidSelection: true,
                isValidDate: true,
            });
        } else if (
            !isValid &&
            val &&
            this.props.minDate &&
            moment(val).isSame(this.props.minDate, 'day')
        ) {
            // The current time is too early, but the day is okay, so just use the min.
            this.setState({
                displayDate: val ? this.getFormattedDate(this.props.minDate) : '',
                isValidSelection: true,
                isValidDate: true,
            });
        } else {
            this.setState({
                displayDate: val ? this.getFormattedDate(val) : '',
                isValidSelection: isValid,
                isValidDate: true,
            });
            if (!isValid) this.renderInvalidSelectionFeedback();
        }

        this.focusTextfield();
    };

    // @ts-expect-error
    handlePickerUpdate = _.debounce((date, isValid) => {
        const isValidSelection = validateMinMax(
            date,
            this.props.minDate,
            this.props.maxDate,
            this.props.shouldOmitTime
        );

        if ((!isValid || !isValidSelection) && !this.initialDateParse) {
            this.renderInvalidSelectionFeedback();
        }

        this.setState({
            isValidDate: isValid,
            isValidSelection: isValidSelection,
        });

        this.initialDateParse = false;
    }, 500);

    renderInvalidSelectionFeedback = _.throttle(
        () => {
            if (this.state.displayDate.length === 0) return;

            this.setState({invalidShakeAnimation: true});

            // @ts-expect-error
            clearTimeout(this.invalidFeedbackTimeout);
            // @ts-expect-error
            this.invalidFeedbackTimeout = setTimeout(() => {
                this.setState({invalidShakeAnimation: false});
            }, this.animateInvalidDuration);
        },
        this.animateInvalidDuration,
        {trailing: false, leading: true}
    );

    getFormattedDate = (date: Date) => {
        return moment(date).format(this.getDisplayFormat());
    };

    focusTextfield = () => {
        if (this.inputRef) this.inputRef.select();
    };

    getDisplayFormat = (): string => {
        if (this.props.displayFormat) return this.props.displayFormat;

        return this.props.shouldOmitTime ? INPUT_FORMAT_WITHOUT_TIME : INPUT_FORMAT_WITH_TIME;
    };

    handleStopPropagation = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
    };
}
